// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { SparseOrganization } from '@/account/api/types';
import { EditSerialPatternsDialog } from '@/account/components/edit-serial-patterns-dialog';
import {
  MAX_SERIAL_PATTERNS,
  serialPatternExamples,
  serialPatternSummary,
} from '@/account/utilities/serial-pattern-display';
import {
  EditRounded,
  InfoOutlined,
  RuleOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';

interface Props {
  organization: SparseOrganization;
}

/** The serial patterns section of the logistics card. */
export function OrganizationSerialPatternsSection(props: Props) {
  const editDialog = useDisclosure();
  const patterns = props.organization.serialPatterns;

  return (
    <Box padding={2}>
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <RuleOutlined fontSize="small" color="action" />
              <Typography variant="subtitle2">Serial patterns</Typography>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditRounded fontSize="small" />}
              onClick={editDialog.open}
            >
              Edit patterns
            </Button>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Serials reported by a third-party logistics provider (3PL) on a
              purchase order (PO) are scanned to enforce your
              organization&apos;s standards.
            </Typography>
            <Tooltip
              arrow
              title="Serials that match one of these patterns will create a new asset automatically. A serial that does not match any pattern will be held in quarantine for user approval."
            >
              <InfoOutlined
                fontSize="small"
                color="action"
                aria-label="How serial patterns are used"
              />
            </Tooltip>
          </Stack>
        </Stack>

        {patterns.length === 0 ? (
          <Box
            padding={3}
            border={1}
            borderColor="divider"
            borderRadius={1}
            textAlign="center"
          >
            <Typography variant="body2" color="text.secondary">
              No patterns are configured. Every reported serial is accepted.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {patterns.map((pattern, index) => {
              const [example] = serialPatternExamples(pattern).values;

              return (
                <Stack
                  key={`${pattern.kind}-${index}`}
                  direction="row"
                  spacing={1.5}
                  padding={1.5}
                  alignItems="center"
                  border={1}
                  borderColor="divider"
                  borderRadius={1}
                >
                  <Typography
                    variant="body2"
                    color="text.primary"
                    flex="0 0 20rem"
                  >
                    {serialPatternSummary(pattern)}
                  </Typography>
                  {example && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ overflowWrap: 'anywhere' }}
                    >
                      Example: {example}
                    </Typography>
                  )}
                </Stack>
              );
            })}
            <Typography variant="caption" color="text.secondary">
              {patterns.length} of {MAX_SERIAL_PATTERNS} patterns
            </Typography>
          </Stack>
        )}
      </Stack>

      <EditSerialPatternsDialog
        organizationKey={props.organization.key}
        patterns={patterns}
        isOpen={editDialog.isOpen}
        onClose={editDialog.close}
      />
    </Box>
  );
}
